import { useState, useEffect, useCallback } from 'react';

export interface SetupChange {
  id: string;
  action: 'created' | 'updated' | 'deleted' | 'activated' | 'deactivated' | 'archived';
  entityType: 'user' | 'role' | 'workflow' | 'object' | 'field' | 'permission' | 'setting';
  entityName: string;
  targetName?: string; // Optional - only for nested entities like fields (e.g., "Account object")
  description: string;
  performedBy: string;
  timestamp: number;
}

const STORAGE_KEY = 'crm_setup_changes';
const MAX_CHANGES = 50;

/**
 * Format a change description for display
 */
const formatChangeDescription = (change: SetupChange): string => {
  const actionMap: Record<SetupChange['action'], string> = {
    created: 'created',
    updated: 'updated',
    deleted: 'deleted',
    activated: 'activated',
    deactivated: 'deactivated',
    archived: 'archived'
  };

  return `${actionMap[change.action]} ${change.entityType}`;
};

/**
 * Get relative time string from timestamp
 */
const getRelativeTime = (timestamp: number): string => {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'Just now';
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
  return new Date(timestamp).toLocaleDateString();
};

/**
 * Hook for managing setup changes with localStorage persistence
 */
export const useSetupChanges = () => {
  const [changes, setChanges] = useState<SetupChange[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsedChanges = JSON.parse(stored);
        console.log('[useSetupChanges] Loaded from localStorage:', parsedChanges.length, 'changes');
        return parsedChanges;
      }
    } catch (error) {
      console.error('[useSetupChanges] Failed to load setup changes:', error);
    }
    return [];
  });

  // Save to localStorage whenever changes update
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(changes));
      console.log('[useSetupChanges] Saved to localStorage:', changes.length, 'changes');
    } catch (error) {
      console.error('[useSetupChanges] Failed to save setup changes:', error);
    }
  }, [changes]);

  /**
   * Log a new setup change
   */
  const logChange = useCallback((change: Omit<SetupChange, 'id' | 'timestamp'>) => {
    const newChange: SetupChange = {
      ...change,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now()
    };

    setChanges(prev => {
      const updated = [newChange, ...prev].slice(0, MAX_CHANGES);
      return updated;
    });
  }, []);

  /**
   * Get recent changes (limited count)
   */
  const getRecentChanges = useCallback((limit: number = 10): SetupChange[] => {
    return changes.slice(0, limit);
  }, [changes]);

  /**
   * Get changes by entity type
   */
  const getChangesByType = useCallback((entityType: SetupChange['entityType']): SetupChange[] => {
    return changes.filter(change => change.entityType === entityType);
  }, [changes]);

  /**
   * Clear all setup changes
   */
  const clearChanges = useCallback(() => {
    console.log('[useSetupChanges] Clearing all changes');
    setChanges([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    changes,
    logChange,
    getRecentChanges,
    getChangesByType,
    clearChanges,
    getRelativeTime,
    formatChangeDescription
  };
};
